import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { Building2, CheckSquare, CircleHelp, CircleSlash2, Gauge, PackagePlus, RefreshCw, Search, ShoppingCart, TrendingUp, TriangleAlert } from "lucide-react";
import { api } from "./api";
import { toast } from "react-toastify";
import "./Pogenerate.css";
import "./InventoryExceptions.css";

const numberValue = (value) => Number(value) || 0;

const getDailySalesRate = (item) => Math.max(
  numberValue(item.last_7day_sale) / 7,
  numberValue(item.last_15ay_sale) / 15,
  numberValue(item.last_30ay_sale) / 30,
);

const getStockStatus = (item) => {
  const stock = numberValue(item.current_stock);
  const dailySale = getDailySalesRate(item);
  if (stock <= 0) return "out";
  if (dailySale <= 0) return stock <= 2 ? "low" : "no_sales";
  if (dailySale > 0 && stock <= dailySale * 7) return "low";
  if (dailySale > 0 && stock > dailySale * 30) return "excess";
  return "healthy";
};

const statusLabels = { all: "All stock", out: "Out of stock", low: "Low stock", healthy: "Healthy", excess: "Excess", no_sales: "No recent sales" };
const exceptionLabels = { all: "All exceptions", negative: "Negative stock", out: "Out of stock", low: "Low cover", excess: "Excess cover", no_sales: "No recent sales", fast: "Fast moving" };

const PurchaseOrders = () => {
  const [activeView, setActiveView] = useState("inventory");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [data, setData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showCalculationGuide, setShowCalculationGuide] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dataRefresh, setDataRefresh] = useState(0);
  const [exceptionRows, setExceptionRows] = useState([]);
  const [exceptionLoading, setExceptionLoading] = useState(false);
  const [exceptionFilter, setExceptionFilter] = useState("all");
  const [exceptionSearch, setExceptionSearch] = useState("");
  const [recommendations, setRecommendations] = useState({ suppliers: [], targetDays: 14 });
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [recommendationSupplier, setRecommendationSupplier] = useState("all");

  const formatDate = (isoDate) => {
    if (!isoDate || Number.isNaN(new Date(isoDate).getTime())) return "N/A";
    return new Intl.DateTimeFormat("en-GB").format(new Date(isoDate)).replaceAll("/", "-");
  };

  const itemKey = (item, index) => String(item.item_id ?? item.item_name ?? index);

  const loadOptions = async () => {
    setLoadingOptions(true);
    setErrorMessage("");
    try {
      const [categoryResponse, supplierResponse] = await Promise.all([
        api.get("/api/po/categories"),
        api.get("/api/po/suppliers"),
      ]);
      const nextCategories = categoryResponse.data.data || [];
      setCategories(nextCategories);
      setSuppliers(supplierResponse.data.data || []);
      setSelectedCategory((current) => current || nextCategories[0]?.category || "");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Unable to load purchase-order options.");
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => { loadOptions(); }, []);

  const loadExceptions = async () => {
    setExceptionLoading(true); setErrorMessage("");
    try { const response = await api.get("/api/po/exceptions/all"); setExceptionRows(Array.isArray(response.data?.data) ? response.data.data : []); }
    catch (error) { setErrorMessage(error.response?.data?.message || "Unable to load inventory exceptions."); }
    finally { setExceptionLoading(false); }
  };

  // Load once when the user first opens the exception workspace.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeView === "exceptions" && !exceptionRows.length) loadExceptions(); }, [activeView]);

  const loadRecommendations = async () => {
    setRecommendationLoading(true); setErrorMessage("");
    try { const response = await api.get("/api/po/recommendations"); setRecommendations(response.data || { suppliers: [], targetDays: 14 }); }
    catch (error) { setErrorMessage(error.response?.data?.message || "Unable to generate supplier recommendations."); }
    finally { setRecommendationLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeView === "recommendations" && !recommendations.suppliers.length) loadRecommendations(); }, [activeView]);

  useEffect(() => {
    if (!selectedCategory) {
      setData([]);
      return;
    }

    let active = true;
    setLoadingData(true);
    setErrorMessage("");
    api.get(`/api/po/getpodta/${encodeURIComponent(selectedCategory)}`)
      .then((response) => {
        if (!active) return;
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        setData(rows);
      })
      .catch((error) => active && setErrorMessage(error.response?.data?.message || "Unable to load purchase-order data."))
      .finally(() => active && setLoadingData(false));
    return () => { active = false; };
  }, [selectedCategory, dataRefresh]);

  const refreshAll = async () => {
    await loadOptions();
    setDataRefresh((current) => current + 1);
  };

  const enrichedData = useMemo(() => data.map((item, index) => ({
    ...item,
    _key: itemKey(item, index),
    _dailySale: getDailySalesRate(item),
    _status: getStockStatus(item),
  })), [data]);

  const summary = useMemo(() => enrichedData.reduce((result, item) => {
    result[item._status] += 1;
    return result;
  }, { out: 0, low: 0, healthy: 0, excess: 0, no_sales: 0 }), [enrichedData]);

  const filteredData = useMemo(() => enrichedData.filter((item) => {
    const matchesSearch = String(item.item_name || "").toLowerCase().includes(searchTerm.trim().toLowerCase());
    return matchesSearch && (statusFilter === "all" || item._status === statusFilter);
  }), [enrichedData, searchTerm, statusFilter]);

  const exceptions = useMemo(() => exceptionRows.map((item, index) => {
    const stock = numberValue(item.current_stock); const dailySale = getDailySalesRate(item); const coverDays = dailySale > 0 ? stock / dailySale : null; const types = [];
    if (stock < 0) types.push("negative"); else if (stock === 0) types.push("out");
    if (stock > 0 && dailySale > 0 && coverDays <= 7) types.push("low");
    if (stock > 0 && dailySale > 0 && coverDays > 30) types.push("excess");
    if (stock > 2 && dailySale === 0) types.push("no_sales");
    if (dailySale >= 3) types.push("fast");
    return { ...item, _key: itemKey(item, index), _stock: stock, _dailySale: dailySale, _coverDays: coverDays, _exceptionTypes: types };
  }).filter(item => item._exceptionTypes.length), [exceptionRows]);
  const exceptionSummary = useMemo(() => Object.keys(exceptionLabels).reduce((result, type) => ({ ...result, [type]: type === "all" ? exceptions.length : exceptions.filter(item => item._exceptionTypes.includes(type)).length }), {}), [exceptions]);
  const filteredExceptions = useMemo(() => exceptions.filter(item => (exceptionFilter === "all" || item._exceptionTypes.includes(exceptionFilter)) && `${item.item_name || ""} ${item.category || ""}`.toLowerCase().includes(exceptionSearch.trim().toLowerCase())), [exceptions, exceptionFilter, exceptionSearch]);
  const visibleRecommendations = useMemo(() => recommendations.suppliers.filter(supplier => recommendationSupplier === "all" || String(supplier.supplierId) === recommendationSupplier), [recommendations, recommendationSupplier]);

  const handleGenerateData = async () => {
    if (!selectedSupplier) return toast.warn("Please select a supplier.");
    const supplier = suppliers.find((item) => String(item.person_id) === String(selectedSupplier));
    if (!window.confirm(`Refresh inventory data for ${supplier?.company_name || "this supplier"}?`)) return;

    setGenerating(true);
    try {
      const result = await api.post("/api/po/generate", { supplierId: selectedSupplier });
      toast.success(result.data?.message || "Inventory data generated successfully.");
      setShowModal(false);
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to generate recommendations.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container-fluid po-page py-3 px-3 px-xl-4">
      {errorMessage && <Alert variant="danger" dismissible onClose={() => setErrorMessage("")}>{errorMessage}</Alert>}

      <div className="po-view-tabs"><button type="button" className={activeView === "inventory" ? "active" : ""} onClick={() => setActiveView("inventory")}><Gauge size={17}/> Inventory status</button><button type="button" className={activeView === "exceptions" ? "active" : ""} onClick={() => setActiveView("exceptions")}><TriangleAlert size={17}/> Inventory exceptions</button><button type="button" className={activeView === "recommendations" ? "active" : ""} onClick={() => setActiveView("recommendations")}><Building2 size={17}/> Supplier PO</button></div>

      {activeView === "inventory" && <>
      <section className="po-controls" aria-label="Purchase order filters">
        <Form.Group>
          <Form.Label>Category</Form.Label>
          <Form.Select value={selectedCategory} onChange={(event) => setSelectedCategory(event.target.value)} disabled={loadingOptions}>
            <option value="">Select category</option>
            {categories.map((category) => <option key={category.category} value={category.category}>{category.category}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group className="po-search-field">
          <Form.Label>Search items</Form.Label>
          <div className="po-input-icon"><Search size={17} /><Form.Control value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Type an item name" /></div>
        </Form.Group>
        <Form.Group>
          <Form.Label>Stock status</Form.Label>
          <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Form.Select>
        </Form.Group>
        <Button variant="outline-secondary" className="po-refresh-button" onClick={refreshAll} disabled={loadingOptions || loadingData} aria-label="Refresh purchase order data">
          <RefreshCw size={17} className={loadingOptions ? "po-spin" : ""} /> Refresh
        </Button>
        <Button className="po-generate-button" onClick={() => setShowModal(true)} disabled={loadingOptions}>
          <PackagePlus size={17} /> Generate PO
        </Button>
        <Button variant="outline-primary" className="po-guide-button" onClick={() => setShowCalculationGuide(true)}>
          <CircleHelp size={17} /> Guide
        </Button>
      </section>

      <section className="po-summary" aria-label="Stock summary">
        {[
          ["out", "Out of stock", summary.out, TriangleAlert],
          ["low", "Low stock", summary.low, ShoppingCart],
          ["healthy", "Healthy stock", summary.healthy, CheckSquare],
          ["excess", "Excess stock", summary.excess, PackagePlus],
          ["no_sales", "No recent sales", summary.no_sales, CircleSlash2],
        ].map(([status, label, value, Icon]) => (
          <button type="button" key={status} className={`po-summary-card po-summary-${status} ${statusFilter === status ? "active" : ""}`} onClick={() => setStatusFilter(statusFilter === status ? "all" : status)}>
            <span className="po-summary-icon"><Icon size={19} /></span><span><small>{label}</small><strong>{value}</strong></span>
          </button>
        ))}
      </section>

      <section className="po-table-card">
        <div className="po-table-toolbar">
          <div><strong>{selectedCategory || "Select a category"}</strong><span>{filteredData.length} items shown</span></div>
          <div className="po-stock-legend"><span>Use the status filter to focus on items needing attention</span></div>
        </div>
        <div className="po-table-wrap">
          <table className="po-table">
            <thead><tr>
              <th>Item</th><th>Status</th><th className="text-end">In stock</th><th className="text-end">Before receipt</th><th className="text-end">Last receipt</th><th>Received</th><th className="text-end">7 days</th><th className="text-end">15 days</th><th className="text-end">30 days</th><th className="text-end">Sales/day</th>
            </tr></thead>
            <tbody>
              {(loadingOptions || loadingData) ? <tr><td colSpan="10" className="po-empty"><Spinner size="sm" /> Loading inventory data…</td></tr> : filteredData.length === 0 ? <tr><td colSpan="10" className="po-empty">{selectedCategory ? "No items match the selected filters." : "Select a category to review inventory status."}</td></tr> : filteredData.map((item) => (
                <tr key={item._key} className={`po-row-${item._status}`}>
                  <td className="po-item-name">{item.item_name}</td>
                  <td><span className={`po-status po-status-${item._status}`}>{statusLabels[item._status]}</span></td>
                  <td className={`text-end ${numberValue(item.current_stock) > 0 ? "po-current-stock" : ""}`}>{numberValue(item.current_stock).toLocaleString("en-IN")}</td>
                  <td className="text-end">{numberValue(item.stock_before_rec).toLocaleString("en-IN")}</td>
                  <td className="text-end">{numberValue(item.last_rec_qty).toLocaleString("en-IN")}</td>
                  <td>{formatDate(item.last_rec_date)}</td>
                  <td className="text-end">{numberValue(item.last_7day_sale).toLocaleString("en-IN")}</td>
                  <td className="text-end">{numberValue(item.last_15ay_sale).toLocaleString("en-IN")}</td>
                  <td className="text-end">{numberValue(item.last_30ay_sale).toLocaleString("en-IN")}</td>
                  <td className="text-end">{item._dailySale.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="po-table-note">Sales/day is calculated from the strongest recent rate: 7-day sales ÷ 7, 15-day sales ÷ 15, or 30-day sales ÷ 30. The unreliable API avg_day_sale value is not used.</div>
      </section>
      </>}

      {activeView === "exceptions" && <>
        <section className="po-exception-controls"><div className="po-input-icon"><Search size={17}/><Form.Control value={exceptionSearch} onChange={event => setExceptionSearch(event.target.value)} placeholder="Search item or category"/></div><Form.Select value={exceptionFilter} onChange={event => setExceptionFilter(event.target.value)}>{Object.entries(exceptionLabels).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</Form.Select><Button variant="outline-secondary" onClick={loadExceptions} disabled={exceptionLoading}><RefreshCw size={16}/> Refresh</Button></section>
        <section className="po-exception-summary">{Object.entries(exceptionLabels).filter(([type]) => type !== "all").map(([type,label]) => <button type="button" key={type} className={`po-exception-card exception-${type} ${exceptionFilter === type ? "active" : ""}`} onClick={() => setExceptionFilter(exceptionFilter === type ? "all" : type)}><small>{label}</small><strong>{exceptionSummary[type] || 0}</strong></button>)}</section>
        <section className="po-table-card"><div className="po-table-toolbar"><div><strong>Inventory exceptions</strong><span>{filteredExceptions.length} actionable items across all categories</span></div><span className="po-exception-note"><TrendingUp size={15}/> Fast moving = 3+ units/day</span></div><div className="po-table-wrap"><table className="po-table po-exception-table"><thead><tr><th>Item</th><th>Category</th><th>Exceptions</th><th className="text-end">In stock</th><th className="text-end">Sales/day</th><th className="text-end">Stock cover</th><th className="text-end">7 days</th><th className="text-end">30 days</th><th>Last received</th></tr></thead><tbody>{exceptionLoading ? <tr><td colSpan="9" className="po-empty"><Spinner size="sm"/> Loading exceptions…</td></tr> : filteredExceptions.length ? filteredExceptions.map(item => <tr key={item._key}><td className="po-item-name">{item.item_name}</td><td>{item.category || "—"}</td><td><div className="po-exception-badges">{item._exceptionTypes.map(type => <span key={type} className={`po-exception-badge exception-${type}`}>{exceptionLabels[type]}</span>)}</div></td><td className="text-end po-current-stock">{item._stock.toLocaleString("en-IN")}</td><td className="text-end">{item._dailySale.toFixed(2)}</td><td className="text-end">{item._coverDays === null ? "—" : `${item._coverDays.toFixed(1)} days`}</td><td className="text-end">{numberValue(item.last_7day_sale).toLocaleString("en-IN")}</td><td className="text-end">{numberValue(item.last_30ay_sale).toLocaleString("en-IN")}</td><td>{formatDate(item.last_rec_date)}</td></tr>) : <tr><td colSpan="9" className="po-empty">No inventory exceptions match the selected filters.</td></tr>}</tbody></table></div></section>
      </>}

      {activeView === "recommendations" && <section className="po-recommendations">
        <div className="po-recommendation-toolbar"><div><strong>Supplier-wise PO recommendations</strong><span>14-day target using 30-day demand, 60-day fallback, and 7-day momentum</span></div><Form.Select value={recommendationSupplier} onChange={event => setRecommendationSupplier(event.target.value)}><option value="all">All suppliers</option>{recommendations.suppliers.map(supplier => <option key={supplier.supplierId} value={supplier.supplierId}>{supplier.supplier}</option>)}</Form.Select><Button variant="outline-primary" onClick={loadRecommendations} disabled={recommendationLoading}>{recommendationLoading ? <Spinner size="sm"/> : <RefreshCw size={16}/>} Generate</Button></div>
        {recommendationLoading && !recommendations.suppliers.length ? <div className="po-empty"><Spinner/> Calculating recommendations…</div> : visibleRecommendations.length ? visibleRecommendations.map(supplier => <article className="po-supplier-card" key={supplier.supplierId}><header><div><span><Building2 size={18}/></span><div><strong>{supplier.supplier}</strong><small>{supplier.items.length} items recommended</small></div></div><div><small>Suggested order</small><strong>{supplier.suggestedPacks.toLocaleString("en-IN")} packs · {supplier.suggestedUnits.toLocaleString("en-IN")} units</strong></div></header><div className="po-table-wrap"><table className="po-table po-recommendation-table"><thead><tr><th>Item</th><th>Category</th><th className="text-end">Current stock</th><th className="text-end">30-day sales</th><th className="text-end">60-day sales</th><th className="text-end">7-day sales</th><th className="text-end">Baseline/day</th><th className="text-end">7-day/day</th><th className="text-end">Forecast/day</th><th className="text-end">Target stock</th><th className="text-end">Pack size</th><th className="text-end">Suggested PO</th></tr></thead><tbody>{supplier.items.map(item => <tr key={item.itemId}><td className="po-item-name">{item.itemName}</td><td>{item.category}</td><td className="text-end">{item.currentStock.toLocaleString("en-IN")}</td><td className="text-end">{item.sales30.toLocaleString("en-IN")}</td><td className="text-end">{item.sales60.toLocaleString("en-IN")}</td><td className="text-end">{item.sales7.toLocaleString("en-IN")}</td><td className="text-end" title={`${item.baselineWindow}-day baseline`}>{item.baselineDaily.toFixed(2)} <small>({item.baselineWindow}d)</small></td><td className="text-end po-momentum-value">{item.momentumDaily.toFixed(2)}</td><td className="text-end">{item.forecastDaily.toFixed(2)}</td><td className="text-end">{item.targetStock.toLocaleString("en-IN")}</td><td className="text-end">{item.packSize.toLocaleString("en-IN")}</td><td className="text-end po-suggested-qty"><strong>{item.suggestedPacks} packs</strong><small>{item.suggestedUnits} units</small></td></tr>)}</tbody></table></div></article>) : <div className="po-empty">No supplier currently requires a suggested order.</div>}
        <div className="po-recommendation-formula"><strong>Formula</strong><span>Standard: 30-day average × 60% + 7-day average × 40%</span><span>Fast-moving: use the full 7-day rate when it is higher</span><span>60-day fallback applies when 30-day sales are zero</span><span>Target = 14 days, rounded to a full pack</span></div>
      </section>}

      <Modal show={showCalculationGuide} onHide={() => setShowCalculationGuide(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Inventory status calculation guide</Modal.Title></Modal.Header>
        <Modal.Body className="po-guide-content">
          <section>
            <h3>1. Sales per day</h3>
            <p>The API average is ignored because it currently returns zero. The screen calculates these three rates and uses the highest one:</p>
            <div className="po-guide-formulas"><code>7-day sales ÷ 7</code><code>15-day sales ÷ 15</code><code>30-day sales ÷ 30</code></div>
            <p className="po-guide-example"><strong>Example:</strong> Sales are 14, 24 and 36. The daily rates are 2.00, 1.60 and 1.20, so Sales/day is <strong>2.00</strong>.</p>
          </section>
          <section>
            <h3>2. Stock status</h3>
            <div className="po-guide-statuses">
              <div><span className="po-status po-status-out">Out of stock</span><p>Current stock is zero or below.</p></div>
              <div><span className="po-status po-status-low">Low stock</span><p>Stock covers 7 days or less. If there are no recent sales, 1–2 units are also low.</p></div>
              <div><span className="po-status po-status-healthy">Healthy</span><p>Stock covers more than 7 days and up to 30 days.</p></div>
              <div><span className="po-status po-status-excess">Excess</span><p>Stock covers more than 30 days.</p></div>
              <div><span className="po-status po-status-no_sales">No recent sales</span><p>All sales windows are zero and current stock is above 2.</p></div>
            </div>
          </section>
        </Modal.Body>
        <Modal.Footer><Button onClick={() => setShowCalculationGuide(false)}>Got it</Button></Modal.Footer>
      </Modal>

      <Modal show={showModal} onHide={() => !generating && setShowModal(false)} backdrop="static" centered>
        <Modal.Header closeButton={!generating}><Modal.Title>Generate PO</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Choose a supplier to refresh its current inventory and sales data.</p>
          <Form.Group><Form.Label>Supplier</Form.Label><Form.Select value={selectedSupplier} onChange={(event) => setSelectedSupplier(event.target.value)} autoFocus><option value="">Select supplier</option>{suppliers.map((supplier) => <option key={supplier.person_id} value={supplier.person_id}>{supplier.company_name}</option>)}</Form.Select></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" disabled={generating} onClick={() => setShowModal(false)}>Cancel</Button><Button variant="primary" disabled={generating || !selectedSupplier} onClick={handleGenerateData}>{generating ? <><Spinner size="sm" /> Generating…</> : "Generate"}</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default PurchaseOrders;
